import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { encodeQr, qrPath, type QrEcc } from '@/lib/qr';

interface QrCodeProps {
  value: string;
  /** Rendered size in CSS pixels; the SVG itself is resolution-independent. */
  size?: number;
  ecc?: QrEcc;
  className?: string;
  title?: string;
}

/**
 * A scannable QR, drawn as one inline SVG path.
 *
 * `dir` is pinned to ltr: the matrix is a coordinate system, not text, and an
 * Arabic page must not mirror it.
 */
export function QrCode({ value, size = 200, ecc = 'M', className, title }: QrCodeProps) {
  const path = useMemo(() => {
    try {
      const matrix = encodeQr(value, ecc);
      return { d: qrPath(matrix), size: matrix.size };
    } catch {
      return null;
    }
  }, [value, ecc]);

  if (!path) return null;

  // Four light modules of quiet zone, as the spec requires for a reliable scan.
  const quiet = 4;
  const extent = path.size + quiet * 2;

  return (
    <svg
      style={{ direction: 'ltr' }}
      role="img"
      aria-label={title ?? value}
      width={size}
      height={size}
      viewBox={`0 0 ${extent} ${extent}`}
      shapeRendering="crispEdges"
      /* Deliberately NOT themed. A QR is an optical target before it is a
         graphic: dark modules on a light ground is what a camera is built to
         decode, and inverting it for the dark theme is the fastest way to make
         a code that will not scan. The brand ink replaces the old slate. */
      className={cn('rounded-lg bg-white', className)}
    >
      {title ? <title>{title}</title> : null}
      <rect width={extent} height={extent} fill="#ffffff" />
      <g transform={`translate(${quiet} ${quiet})`}>
        <path d={path.d} fill="#080604" />
      </g>
    </svg>
  );
}
